import { useState } from "react";
import { modals } from "@mantine/modals";
import {
  ActionIcon,
  Button,
  Group,
  Image,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { pickAndUploadIcons } from "./upload-icons";
import { VariantSelect, type LayoutVariant } from "./variant-select";

export interface SingleIconText {
  iconSrc: string;
  iconAlt: string;
  text: string;
  variant: LayoutVariant;
}

function SingleIconTextForm({
  initial,
  modalId,
  resolve,
  pageId,
  submitLabel,
}: {
  initial: SingleIconText;
  modalId: string;
  resolve: (val: SingleIconText | null) => void;
  pageId?: string;
  submitLabel: string;
}) {
  const [value, setValue] = useState(initial);
  const replaceIcon = async () => {
    if (!pageId) return;
    const [ic] = await pickAndUploadIcons(pageId);
    if (!ic) return;
    setValue((v) => ({ ...v, iconSrc: ic.src, iconAlt: ic.alt }));
  };

  return (
    <Stack gap="md">
      <Group wrap="nowrap" align="flex-start" gap="sm">
        <Stack gap={4} align="center" style={{ flexShrink: 0 }}>
          {value.iconSrc ? (
            <Image
              src={value.iconSrc}
              alt={value.iconAlt}
              w={48}
              h={48}
              fit="contain"
              radius="sm"
              style={{
                border: "1px solid var(--mantine-color-default-border)",
              }}
            />
          ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 4,
                background: "var(--mantine-color-gray-1)",
              }}
            />
          )}
          {pageId ? (
            <Tooltip
              label={value.iconSrc ? "Replace icon" : "Pick icon"}
              withArrow
            >
              <ActionIcon
                size="xs"
                variant="default"
                onClick={replaceIcon}
                aria-label="Replace icon"
              >
                <IconRefresh size={12} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Stack>
        <Stack gap={4} style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">
            Icon + text on one line.
          </Text>
          <Textarea
            size="xs"
            label="Text"
            autosize
            minRows={2}
            value={value.text}
            onChange={(e) =>
              setValue((v) => ({ ...v, text: e.currentTarget.value }))
            }
          />
          <VariantSelect
            value={value.variant}
            onChange={(variant) => setValue((v) => ({ ...v, variant }))}
          />
        </Stack>
      </Group>
      <Group justify="flex-end">
        <Button
          variant="default"
          size="xs"
          onClick={() => {
            // Resolve BEFORE close — onClose fires settle(null) sync.
            resolve(null);
            modals.close(modalId);
          }}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          onClick={() => {
            resolve(value);
            modals.close(modalId);
          }}
        >
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

export function openSingleIconText(
  initial: SingleIconText,
  options: { pageId?: string; title?: string; mode?: "insert" | "edit" } = {},
): Promise<SingleIconText | null> {
  return new Promise((resolve) => {
    const modalId = `single-icon-text-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let settled = false;
    const settle = (val: SingleIconText | null) => {
      if (settled) return;
      settled = true;
      resolve(val);
    };
    const submitLabel = options.mode === "edit" ? "Save" : "Insert";
    modals.open({
      modalId,
      title: options.title ?? "Configure",
      size: "md",
      withCloseButton: true,
      closeOnEscape: true,
      closeOnClickOutside: false,
      onClose: () => settle(null),
      children: (
        <SingleIconTextForm
          initial={initial}
          modalId={modalId}
          resolve={settle}
          pageId={options.pageId}
          submitLabel={submitLabel}
        />
      ),
    });
  });
}
